import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { KeyRound } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_volunteer-dispatch-6/artifacts/8p87dbov_hope-to-home-350x66.png";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
    if (pw !== pw2) return toast.error("Passwords don't match.");
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated. Please sign in.");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen surface-public grain flex flex-col items-center justify-center px-4">
      <Link to="/login" className="mb-8 focus-ring rounded-md" data-testid="reset-back-to-login-link">
        <img src={LOGO_URL} alt="Hope to Home" className="h-12 w-auto" />
      </Link>
      <div className="w-full max-w-sm card-elevated rounded-xl p-7 sm:p-8" data-testid="reset-password-card">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Set a new password</h1>
        <p className="text-sm text-slate-500 mt-1.5">
          Choose something at least 8 characters long that you&apos;ll remember.
        </p>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <Label htmlFor="reset-pw" className="text-sm font-semibold">New password</Label>
            <Input
              id="reset-pw"
              data-testid="reset-password-input"
              type="password"
              autoComplete="new-password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              required
              minLength={8}
              className="mt-1.5 text-base min-h-[44px]"
            />
          </div>
          <div>
            <Label htmlFor="reset-pw2" className="text-sm font-semibold">Confirm new password</Label>
            <Input
              id="reset-pw2"
              data-testid="reset-password-confirm-input"
              type="password"
              autoComplete="new-password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              required
              minLength={8}
              className="mt-1.5 text-base min-h-[44px]"
            />
          </div>
          <Button
            type="submit"
            data-testid="reset-submit-button"
            disabled={submitting}
            className="w-full min-h-[48px] bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold"
          >
            <KeyRound className="w-4 h-4 mr-2" />
            {submitting ? "Saving…" : "Set new password"}
          </Button>
        </form>
        <p className="text-xs text-slate-500 mt-6 text-center">
          <Link to="/forgot-password" className="text-brand-blue font-semibold hover:underline" data-testid="reset-request-new-link">
            Need a new reset link?
          </Link>
        </p>
      </div>
    </div>
  );
}
